import { Tooltip } from "antd";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "V2.0/common/table";
import { Badge } from "V2.0/common/badge";
import { CAMPAIGN_STATUS_LABEL_COLORS } from "../SingleCampaigns";
import {
  ArrowUpRightIcon,
  ClockIcon,
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { ConversationSourceIcon } from "V2.0/HomePages/Threads/components/conversation_item";
import CampaignStatusBadge, {
  STATUS,
} from "V2.0/HomePages/Campaigns/common/CampaignStatusBadge";
import { Link } from "react-router-dom";
import { capitalizeFirstLetter } from "V2.0/utils";
import { Button } from "V2.0/common/button";

const formatFollowUpTime = (timestamp) => {
  const date = new Date(Number(timestamp));
  return dayjs(date).format("MMM DD, YYYY, HH:mm");
};

function getEmailCampaignObjectViewModel(campaign) {
  return {
    campaignType: campaign.sourceOfConversation,
    title: campaign.toEmail || campaign.userIdentifier,
    campaignStatus: campaign.status,
    assistant: campaign.assistant,
    lastAttemptStatus: capitalizeFirstLetter(campaign.emailStatus),
    hasUserResponded: campaign.userResponded,
    nextFollowUpTime: campaign.nextFollowUpTime,
    createdAt: campaign.createdAt,
  };
}

function getCallCampaignObjectViewModel(campaign) {
  return {
    campaignType: campaign.sourceOfConversation,
    title: campaign.userIdentifier,
    campaignStatus: campaign.status,
    assistant: campaign.assistant,
    lastAttemptStatus: campaign.callStatus,
    hasUserResponded: campaign.userResponded,
    nextFollowUpTime: campaign.nextFollowUpTime,
    createdAt: campaign.createdAt,
  };
}

function getSmsCampaignObjectViewModel(campaign) {
  return {
    campaignType: campaign.sourceOfConversation,
    title: campaign.userIdentifier,
    campaignStatus: campaign.status,
    assistant: campaign.assistant,
    lastAttemptStatus: campaign.callStatus,
    hasUserResponded: campaign.userResponded,
    nextFollowUpTime: campaign.nextFollowUpTime,
    createdAt: campaign.createdAt,
  };
}

function getWhatsappCampaignObjectViewModel(campaign) {
  return {
    campaignType: campaign.sourceOfConversation,
    title: campaign.userIdentifier,
    campaignStatus: campaign.status,
    assistant: campaign.assistant,
    lastAttemptStatus: campaign.callStatus,
    hasUserResponded: campaign.userResponded,
    nextFollowUpTime: campaign.nextFollowUpTime,
    createdAt: campaign.createdAt,
  };
}

function getCampaignViewModel(campaign) {
  switch (campaign.sourceOfConversation) {
    case "OUTBOUNDVOICE":
      return getCallCampaignObjectViewModel(campaign);
    case "SMS":
      return getSmsCampaignObjectViewModel(campaign);
    case "WHATSAPP":
      return getWhatsappCampaignObjectViewModel(campaign);
    case "EMAIL":
      return getEmailCampaignObjectViewModel(campaign);
    default:
      return getCallCampaignObjectViewModel(campaign);
  }
}

// Exported helper (bulk-campaign-report-ui-port, Task 6) so the header-column labels can be shared
// with CampaignLoading.tsx's skeleton and the bulk-campaign drill-down page, instead of each place
// hardcoding its own copy that can drift out of sync. Scoped narrowly per Task 6 Step 3 — this is
// the only new export this port adds to this file; the Frontend diff's follow-up-scheduling/
// email-engagement additions (empty-timestamp guard, "Opened" status override, followUpHistory/
// lastFollowUp threading, "First call"/"N follow-ups made" labels, showFirstCallLabel) are
// deliberately NOT ported — out of scope, belong to unrelated features.
export function campaignObjectColumns({ hideAssistant = false, showAction = false } = {}) {
  return [
    "Campaign",
    ...(hideAssistant ? [] : ["Assistant"]),
    "Last Attempt Status",
    "Follow Up",
    "Created At",
    // "Conversation",
    ...(showAction ? ["Action"] : []),
  ];
}

export default function CampaignObjectsTable(props) {
  /*
   * onCampaignReport is opt-in, following onDelete above it. This table is shared by the Single
   * Campaigns list and the bulk-campaign drill-down; only the former opens a per-contact report,
   * so the caller decides. Omitted, the rows keep their current inert behaviour exactly.
   */
  const {
    campaignObjects,
    assistantsRecord,
    onDelete,
    onCampaignReport,
    isViewOnly,
    // hideAssistant: new prop (bulk-campaign-report-ui-port, Task 6) — the bulk-campaign drill-down
    // page passes this to omit the Assistant column, since every row there already belongs to the
    // same assistant (shown once in the page heading instead).
    hideAssistant = false,
  } = props;

  const isRowClickable = Boolean(onCampaignReport);

  return (
    // overflow-scroll -> overflow-auto (bulk-campaign-report-ui-port, Task 6 Step 3): avoids forcing
    // scrollbars to always render when content fits.
    // <div className="flex-1 h-full min-h-0 overflow-scroll w-full">
    <div className="flex-1 h-full min-h-0 overflow-auto w-full">
      <Table>
        {/* Old hardcoded TableHeader list. Replaced with campaignObjectColumns() above so this
            header and CampaignLoading.tsx's skeleton can't drift out of sync, and so hideAssistant
            can conditionally omit the Assistant column. (bulk-campaign-report-ui-port, Task 6) */}
        {/* <TableHead>
          <TableRow>
            <TableHeader>Campaign</TableHeader>
            <TableHeader>Assistant</TableHeader>
            <TableHeader>Last Attempt Status</TableHeader>
            <TableHeader>Follow Up</TableHeader>
            <TableHeader>Created At</TableHeader>
            {onDelete && <TableHeader>Action</TableHeader>}
          </TableRow>
        </TableHead> */}
        <TableHead>
          <TableRow>
            {campaignObjectColumns({
              hideAssistant,
              showAction: Boolean(onDelete),
            }).map((label) => (
              <TableHeader key={label}>{label}</TableHeader>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {campaignObjects.map((item, index) => {
            const areConversationsAvailable = item.conversation.length > 0;
            const {
              title,
              campaignType,
              campaignStatus,
              lastAttemptStatus,
              hasUserResponded,
              nextFollowUpTime,
              assistant,
              createdAt,
            } = getCampaignViewModel(item);

            // Stops the click reaching the row handler above, which would otherwise open the
            // report modal behind the delete confirmation.
            const handleDelete = function (e) {
              e.stopPropagation();
              onDelete({
                campaignId: item._id,
                userNumber: title,
              });
            };

            return (
              <TableRow
                key={index}
                className={
                  isRowClickable
                    ? "hover:bg-gray-50 hover:cursor-pointer"
                    : undefined
                }
                onClick={
                  isRowClickable ? () => onCampaignReport(item) : undefined
                }
              >
                <TableCell className="">
                  <div className="">
                    <div className="flex items-center gap-x-2">
                      {ConversationSourceIcon[campaignType]}
                      <p className="text-sm font-medium">{title}</p>
                      {campaignStatus && campaignStatus === STATUS.ARCHIVED && (
                        <CampaignStatusBadge status={campaignStatus} />
                      )}
                    </div>
                  </div>
                </TableCell>
                {/* Conditionally omitted when hideAssistant is set (bulk-campaign-report-ui-port,
                    Task 6) — the bulk-campaign drill-down page already shows the assistant once in
                    its page heading, so repeating it per-row would be redundant. */}
                {!hideAssistant && (
                  <TableCell className="truncate max-w-[150px]">
                    <Tooltip title={assistantsRecord[assistant]?.name}>
                      {assistantsRecord[assistant]?.name ?? (
                        <Badge>Not Found</Badge>
                      )}
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell>
                  {" "}
                  <Badge
                    color={CAMPAIGN_STATUS_LABEL_COLORS[lastAttemptStatus]}
                  >
                    {lastAttemptStatus}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {hasUserResponded === true ? (
                      <Badge color="emerald">User Responded</Badge>
                    ) : !nextFollowUpTime ||
                      campaignStatus === STATUS.ARCHIVED ? (
                      <Badge>No follow-up scheduled</Badge>
                    ) : (
                      <Badge color="amber">
                        <ClockIcon className="size-4" />
                        {formatFollowUpTime(nextFollowUpTime)}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{formatFollowUpTime(createdAt)}</TableCell>
                {/* <TableCell>
                  {areConversationsAvailable ? (
                    <div className="flex gap-x-1 items-center underline hover:cursor-pointer">
                      <Link
                        to={`/threads?projectId=${item.workspace}?tab=All`}
                        state={{
                          from: "campaign",
                          data: { userIdentifier: item?.userIdentifier },
                        }}
                        className="text-blue-500 hover:cursor-pointer"
                      >
                        View Thread
                      </Link>
                      <ArrowUpRightIcon className="text-blue-500 size-2" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-x-2 text-gray-600 hover:cursor-pointer">
                      <p>No thread</p>
                      <Tooltip title="Thread doesn't exist">
                        <InformationCircleIcon className="size-4" />
                      </Tooltip>
                    </div>
                  )}
                </TableCell> */}
                {onDelete && (
                  <TableCell>
                    {!isViewOnly && (
                      <Button
                        outline
                        onClick={handleDelete}
                      >
                        <TrashIcon />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
